import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  X,
  RefreshCw,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  type Site,
  type AllowedEmail,
  type WPRole,
  WP_ROLES,
  parseAllowedDomains,
  parseAllowedEmails,
} from './shared'
import { useUpdateSite } from '../../hooks/useSites'

interface UsersTabProps {
  site: Site
}

export function UsersTab({ site }: UsersTabProps) {
  // Use local state for immediate UI updates
  const [domains, setDomains] = useState<string[]>(() => parseAllowedDomains(site.allowed_domains))
  const [emails, setEmails] = useState<AllowedEmail[]>(() => parseAllowedEmails(site.allowed_emails))

  // Sync local state when site prop changes (e.g., switching sites)
  useEffect(() => {
    setDomains(parseAllowedDomains(site.allowed_domains))
    setEmails(parseAllowedEmails(site.allowed_emails))
  }, [site.domain, site.allowed_domains, site.allowed_emails])

  const updateSite = useUpdateSite()
  const [newEmail, setNewEmail] = useState('')
  const [newEmailRole, setNewEmailRole] = useState<WPRole>('administrator')
  const [newDomain, setNewDomain] = useState('')
  const [isFetchingUsers, setIsFetchingUsers] = useState(false)
  const [fetchedUsers, setFetchedUsers] = useState<Array<{ email: string; display_name?: string; roles?: string[] }>>([])
  const [showFetchDialog, setShowFetchDialog] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())

  const handleAddEmail = async () => {
    const email = newEmail.trim().toLowerCase()
    if (!email) return
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }
    if (emails.some(e => e.email === email)) {
      toast.error('Email already in list')
      return
    }

    const newEntry: AllowedEmail = { email, role: newEmailRole }
    const newEmails = [...emails, newEntry]
    setEmails(newEmails) // Optimistic update
    setNewEmail('')
    setNewEmailRole('administrator') // Reset to default

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { allowed_emails: newEmails }
      })
      toast.success(`Email added as ${newEmailRole}`)
    } catch (err) {
      setEmails(emails) // Rollback on error
      toast.error(`Failed to add email: ${String(err)}`)
    }
  }

  const handleRemoveEmail = async (emailToRemove: string) => {
    const newEmails = emails.filter(e => e.email !== emailToRemove)
    setEmails(newEmails) // Optimistic update

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { allowed_emails: newEmails }
      })
      toast.success('Email removed')
    } catch (err) {
      setEmails(emails) // Rollback on error
      toast.error(`Failed to remove email: ${String(err)}`)
    }
  }

  const handleUpdateEmailRole = async (email: string, newRole: WPRole) => {
    const previousEmails = [...emails]
    const newEmails = emails.map(e =>
      e.email === email ? { ...e, role: newRole } : e
    )
    setEmails(newEmails) // Optimistic update

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { allowed_emails: newEmails }
      })
      toast.success(`Role updated to ${newRole}`)
    } catch (err) {
      setEmails(previousEmails) // Rollback on error
      toast.error(`Failed to update role: ${String(err)}`)
    }
  }

  const handleAddDomain = async () => {
    let domain = newDomain.trim().toLowerCase()
    if (!domain) return
    // Remove @ prefix if user included it
    if (domain.startsWith('@')) domain = domain.slice(1)
    if (domains.includes(domain)) {
      toast.error('Domain already in list')
      return
    }

    const newDomains = [...domains, domain]
    setDomains(newDomains) // Optimistic update
    setNewDomain('')

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { allowed_domains: newDomains }
      })
      toast.success('Domain added')
    } catch (err) {
      setDomains(domains) // Rollback on error
      toast.error(`Failed to add domain: ${String(err)}`)
    }
  }

  const handleRemoveDomain = async (domainToRemove: string) => {
    const newDomains = domains.filter(d => d !== domainToRemove)
    setDomains(newDomains) // Optimistic update

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { allowed_domains: newDomains }
      })
      toast.success('Domain removed')
    } catch (err) {
      setDomains(domains) // Rollback on error
      toast.error(`Failed to remove domain: ${String(err)}`)
    }
  }

  // Fetch users from WordPress site
  const handleFetchUsers = async () => {
    setIsFetchingUsers(true)
    try {
      const response = await fetch(`/api/auth-server/sites/${site.domain}/remote/users`)
      if (!response.ok) throw new Error('Failed to fetch users')
      const data = await response.json() as { users?: Array<{ email: string; display_name?: string; roles?: string[] }> }
      if (data.users && data.users.length > 0) {
        setFetchedUsers(data.users)
        // Pre-select users not already in the allowed list
        const existingEmails = new Set(emails.map(e => e.email.toLowerCase()))
        const newSelection = new Set<string>()
        data.users.forEach((u) => {
          if (!existingEmails.has(u.email.toLowerCase())) {
            newSelection.add(u.email.toLowerCase())
          }
        })
        setSelectedUsers(newSelection)
        setShowFetchDialog(true)
      } else {
        toast.info('No admin/editor users found on this site')
      }
    } catch (err) {
      toast.error(`Failed to fetch users: ${String(err)}`)
    } finally {
      setIsFetchingUsers(false)
    }
  }

  // Add selected users from fetch dialog
  const handleAddSelectedUsers = async () => {
    const usersToAdd = fetchedUsers.filter(u => selectedUsers.has(u.email.toLowerCase()))
    if (usersToAdd.length === 0) {
      toast.error('No users selected')
      return
    }

    const newEmails = [...emails]
    for (const user of usersToAdd) {
      if (!emails.some(e => e.email.toLowerCase() === user.email.toLowerCase())) {
        // Map WordPress role to our role type
        const wpRole = user.roles?.[0] || 'subscriber'
        const role = WP_ROLES.find(r => r.value === wpRole)?.value || 'administrator'
        newEmails.push({ email: user.email.toLowerCase(), role })
      }
    }

    setEmails(newEmails) // Optimistic update
    setShowFetchDialog(false)

    try {
      await updateSite.mutateAsync({
        domain: site.domain,
        data: { allowed_emails: newEmails }
      })
      toast.success(`Added ${usersToAdd.length} user${usersToAdd.length !== 1 ? 's' : ''}`)
    } catch (err) {
      setEmails(emails) // Rollback on error
      toast.error(`Failed to add users: ${String(err)}`)
    }
  }

  const toggleUserSelection = (email: string) => {
    const newSelection = new Set(selectedUsers)
    if (newSelection.has(email.toLowerCase())) {
      newSelection.delete(email.toLowerCase())
    } else {
      newSelection.add(email.toLowerCase())
    }
    setSelectedUsers(newSelection)
  }

  return (
    <div className="space-y-6">
      {/* Allowed Email Domains */}
      <div className="space-y-2">
        <div className="font-medium">Allowed Email Domains</div>
        <div className="text-sm text-muted-foreground mb-2">
          Users with these email domains can sign in (jezweb.net always allowed)
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          <Badge variant="secondary" className="opacity-50">@jezweb.net</Badge>
          {domains.map((domain) => (
            <Badge key={domain} variant="secondary" className="flex items-center gap-1 pr-1">
              @{domain}
              <button
                onClick={() => handleRemoveDomain(domain)}
                className="ml-1 hover:bg-muted rounded-full p-0.5"
                disabled={updateSite.isPending}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add domain (e.g. company.com)"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
            className="flex-1"
          />
          <Button
            size="sm"
            onClick={handleAddDomain}
            disabled={updateSite.isPending || !newDomain.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Allowed Specific Emails */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-medium">Allowed Emails</div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleFetchUsers}
            disabled={isFetchingUsers}
          >
            {isFetchingUsers ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Fetch from Site
          </Button>
        </div>
        <div className="text-sm text-muted-foreground mb-2">
          Specific email addresses that can sign in with assigned WordPress roles
        </div>
        {emails.length > 0 && (
          <div className="space-y-2 mb-3">
            {emails.map((entry) => (
              <div
                key={entry.email}
                className="flex items-center gap-2 p-2 rounded-lg border bg-card"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-mono truncate">{entry.email}</div>
                </div>
                <Select
                  value={entry.role}
                  onValueChange={(value) => handleUpdateEmailRole(entry.email, value as WPRole)}
                  disabled={updateSite.isPending}
                >
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WP_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  onClick={() => handleRemoveEmail(entry.email)}
                  className="p-1 hover:bg-muted rounded-full"
                  disabled={updateSite.isPending}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            placeholder="Add email (e.g. user@example.com)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
            className="flex-1"
          />
          <Select
            value={newEmailRole}
            onValueChange={(value) => setNewEmailRole(value as WPRole)}
          >
            <SelectTrigger className="w-32 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WP_ROLES.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={handleAddEmail}
            disabled={updateSite.isPending || !newEmail.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Fetch Users Dialog */}
      {showFetchDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg border shadow-lg max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b">
              <h3 className="font-medium">Add Users from Site</h3>
              <p className="text-sm text-muted-foreground">
                Select users to add to the allowed list
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-2">
                {fetchedUsers.map((user) => {
                  const isExisting = emails.some(e => e.email.toLowerCase() === user.email.toLowerCase())
                  return (
                    <div
                      key={user.email}
                      className={`flex items-center gap-3 p-2 rounded-lg border ${
                        isExisting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'
                      }`}
                      onClick={() => !isExisting && toggleUserSelection(user.email)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.email.toLowerCase())}
                        disabled={isExisting}
                        onChange={() => {}}
                        className="h-4 w-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {user.display_name || user.email}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0 capitalize">
                        {user.roles?.[0] || 'subscriber'}
                      </Badge>
                      {isExisting && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          Already added
                        </Badge>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="p-4 border-t flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowFetchDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddSelectedUsers} disabled={selectedUsers.size === 0}>
                Add {selectedUsers.size} User{selectedUsers.size !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Note */}
      <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
        <strong>Note:</strong> @jezweb.net emails are always allowed and cannot be removed. Users from allowed domains or specific emails will be auto-created on first OAuth login.
      </div>
    </div>
  )
}
